import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, CheckCircle2, Hammer, XCircle } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import { SeloUrgencia } from './PrazoSolicitacao';
import { recarregarVistos } from './SeloVisualizado';
import { daEquipeDaSolicitacao, podeEntrarNaCentral, podeReceberPedido } from './permissoesSolicitacao';
import type { Profile } from '../../types';

/**
 * Aviso na tela da Central de Solicitações (#6544 e #6547), só para pedidos a Financeiro,
 * Faturamento, Comercial e Suporte técnico. Um aviso por vez, guardado no banco (avisos_solicitacao), então
 * vale em qualquer computador. Janela que trava a tela: só some quando a pessoa escolhe uma opção.
 *   - 'recebida': para quem vai atender — "Resolver agora" ou "Lembre-me" (15 min a 4h);
 *   - 'em_desenvolvimento' / 'concluido' / 'rejeitado': para quem pediu — "Ver solicitação" ou "Ok".
 */

type TipoAviso = 'recebida' | 'em_desenvolvimento' | 'concluido' | 'rejeitado';

interface Aviso {
  id: number;
  tarefa_id: number;
  tipo: TipoAviso;
  texto: string | null;
  quem_nome: string | null;
  tarefa: { titulo: string; prioridade: string; vencimento: string | null; descricao: string | null; solicitante_id: string | null; tags?: string[] | null; solicitacao_status: string | null; departamento_solicitacao: string | null } | null;
}

const RECARREGAR_MS = 60_000;
const ADIAMENTOS = [
  { rotulo: '15 min', min: 15 }, { rotulo: '30 min', min: 30 }, { rotulo: '1h', min: 60 },
  { rotulo: '2h', min: 120 }, { rotulo: '4h', min: 240 },
];
const PRIORIDADE: Record<string, string> = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgencia: 'Urgência' };

const CABECALHO: Record<TipoAviso, { titulo: string; icone: React.ReactNode; cor: string }> = {
  recebida: { titulo: 'Nova solicitação para você', icone: <BellRing size={18} aria-hidden="true" />, cor: 'bg-primary text-white' },
  em_desenvolvimento: { titulo: 'Sua solicitação começou a ser feita', icone: <Hammer size={18} aria-hidden="true" />, cor: 'bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300' },
  concluido: { titulo: 'Sua solicitação foi concluída', icone: <CheckCircle2 size={18} aria-hidden="true" />, cor: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' },
  rejeitado: { titulo: 'Sua solicitação foi rejeitada', icone: <XCircle size={18} aria-hidden="true" />, cor: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300' },
};

const resumo = (t: string | null | undefined, n = 220) => {
  const s = (t || '').replace(/^✅ Conclusão:\s*/, '').replace(/\s+/g, ' ').trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};
const dataBR = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR');

const AvisosSolicitacao: React.FC = () => {
  const { user } = useAuth();
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [quemPediu, setQuemPediu] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const primeiroBotaoRef = useRef<HTMLButtonElement>(null);
  // "Mandar para outra pessoa" (#6558): só quem é da equipe da solicitação.
  const [mandando, setMandando] = useState(false);
  const [pessoasDaEquipe, setPessoasDaEquipe] = useState<Pick<Profile, 'id' | 'full_name'>[]>([]);
  const [paraQuem, setParaQuem] = useState('');

  const carregar = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase.from('avisos_solicitacao')
      .select('id, tarefa_id, tipo, texto, quem_nome, tarefa:tarefas(titulo, prioridade, vencimento, descricao, solicitante_id, tags, solicitacao_status, departamento_solicitacao)')
      .eq('user_id', user.id)
      .is('visto_em', null)
      .lte('lembrar_em', new Date().toISOString())
      .order('criado_em', { ascending: true })
      .limit(10);
    const lista = (data ?? []) as unknown as Aviso[];
    // "Nova solicitação" de algo já concluído ou rejeitado não tem o que resolver: não aparece.
    // Não marca como visto: o selo da Central continua dizendo a verdade ("Não visualizado").
    const prox = lista.find(a => !(a.tipo === 'recebida' && ['concluido', 'rejeitado'].includes(a.tarefa?.solicitacao_status || ''))) ?? null;
    setAviso(prox);
    setQuemPediu(null);
    setMandando(false);
    setParaQuem('');
    if (prox?.tipo === 'recebida' && prox.tarefa?.solicitante_id) {
      const { data: p } = await supabase.from('profiles').select('full_name').eq('id', prox.tarefa.solicitante_id).maybeSingle();
      setQuemPediu(p?.full_name ?? null);
    } else if (prox?.tipo === 'recebida' && prox.tarefa?.tags?.includes('vick')) {
      setQuemPediu('Vick'); // #6597: tarefa aberta pela Vick, sem solicitante
    }
  }, [user?.id]);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, RECARREGAR_MS);
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [carregar]);

  // Leva o foco para a janela: quem usa teclado não fica preso atrás dela.
  useEffect(() => { if (aviso) primeiroBotaoRef.current?.focus(); }, [aviso?.id]);

  if (!user?.id || !aviso) return null;

  const responder = async (campos: { visto_em?: string; lembrar_em?: string }) => {
    setSalvando(true);
    const { error } = await supabase.from('avisos_solicitacao').update(campos).eq('id', aviso.id);
    setSalvando(false);
    if (error) console.error('Erro ao responder o aviso da solicitação:', error);
    else recarregarVistos(); // o selo "Visualizado" da Central acompanha
    // Some da tela mesmo se a gravação falhar: volta no próximo carregamento.
    setAviso(null);
    setTimeout(carregar, 300);
  };
  const visto = () => responder({ visto_em: new Date().toISOString() });
  const abrir = () => { abrirSolicitacao(String(aviso.tarefa_id)); visto(); };
  const adiar = (min: number) => responder({ lembrar_em: new Date(Date.now() + min * 60_000).toISOString() });
  const abrirMandar = async () => {
    setMandando(true);
    const { data } = await supabase.from('profiles').select('id, full_name, role, status, departamento, departamentos').order('full_name');
    const tarefa = { departamento_solicitacao: (aviso.tarefa?.departamento_solicitacao || null) as Profile['departamento'] } as any;
    setPessoasDaEquipe(((data ?? []) as Profile[]).filter(u =>
      u.id !== user.id && u.status !== 'inactive' && podeEntrarNaCentral(u) && podeReceberPedido(u) && daEquipeDaSolicitacao(tarefa, u)));
  };
  const mandar = async () => {
    if (!paraQuem) return;
    setSalvando(true);
    // Trocar o responsável dispara o aviso para a outra pessoa (gatilho trg_avisar_novo_responsavel).
    const { data, error } = await supabase.from('tarefas').update({ responsavel_id: paraQuem }).eq('id', aviso.tarefa_id).select('id');
    setSalvando(false);
    if (error || !data?.length) { console.error('Erro ao mandar a solicitação:', error); return; }
    visto();
  };

  const cab = CABECALHO[aviso.tipo];
  const t = aviso.tarefa;
  const detalhe = aviso.tipo === 'recebida' ? resumo(t?.descricao) : resumo(aviso.texto);

  return (
    // Trava a tela (pedido de 03/11/2026): sem clique fora nem Esc; só sai escolhendo uma opção.
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div
      role="alertdialog" aria-modal="true" aria-labelledby="aviso-solicitacao-titulo"
      className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <div className={`flex items-center gap-2 px-4 py-3 ${cab.cor}`}>
        {cab.icone}
        <h2 id="aviso-solicitacao-titulo" className="text-sm font-bold flex-1">{cab.titulo}</h2>
      </div>

      <div className="px-4 py-3 space-y-2">
        <button
          onClick={abrir}
          className="text-left text-sm font-semibold text-gray-900 dark:text-white underline decoration-dotted underline-offset-2 hover:text-primary dark:hover:text-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm line-clamp-2"
        >
          #{aviso.tarefa_id} {t?.titulo || 'Solicitação'}
        </button>
        {aviso.tipo === 'recebida' ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
            {quemPediu && <span>Pedido de <strong>{quemPediu}</strong></span>}
            {t?.prioridade === 'urgencia' ? <SeloUrgencia /> : t?.prioridade && <span>Prioridade <strong>{PRIORIDADE[t.prioridade] || t.prioridade}</strong></span>}
            {t?.vencimento && <span>Prazo <strong>{dataBR(t.vencimento)}</strong></span>}
          </div>
        ) : aviso.quem_nome && (
          <p className="text-xs text-gray-600 dark:text-gray-300">
            {aviso.tipo === 'em_desenvolvimento' ? 'Quem está fazendo' : aviso.tipo === 'concluido' ? 'Concluída por' : 'Rejeitada por'}: <strong>{aviso.quem_nome}</strong>
          </p>
        )}
        {detalhe && (
          <p className="text-xs text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/40 rounded-lg px-3 py-2 whitespace-pre-line">
            {aviso.tipo === 'concluido' && <span className="font-semibold">O que foi feito: </span>}
            {aviso.tipo === 'rejeitado' && <span className="font-semibold">Motivo: </span>}
            {detalhe}
          </p>
        )}
      </div>

      {aviso.tipo === 'recebida' ? (
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
          <button ref={primeiroBotaoRef} onClick={abrir} disabled={salvando} className="w-full px-3 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            Resolver agora
          </button>
          {mandando ? (
            <div className="flex items-center gap-1.5">
              <label htmlFor="aviso-mandar-para" className="sr-only">Mandar para</label>
              <select id="aviso-mandar-para" value={paraQuem} onChange={e => setParaQuem(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 dark:text-white">
                <option value="">{pessoasDaEquipe.length ? 'Escolha quem da equipe…' : 'Carregando…'}</option>
                {pessoasDaEquipe.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
              <button onClick={mandar} disabled={!paraQuem || salvando}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">Mandar</button>
              <button onClick={() => setMandando(false)} disabled={salvando}
                className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">Voltar</button>
            </div>
          ) : (
            <button onClick={abrirMandar} disabled={salvando}
              className="w-full px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              Mandar para outra pessoa da equipe
            </button>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Lembre-me em</span>
            {ADIAMENTOS.map(a => (
              <button key={a.min} onClick={() => adiar(a.min)} disabled={salvando}
                className="px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                {a.rotulo}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
          <button ref={primeiroBotaoRef} onClick={abrir} disabled={salvando} className="px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            Ver solicitação
          </button>
          <button onClick={visto} disabled={salvando} className="px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            Ok
          </button>
        </div>
      )}
    </div>
    </div>
  );
};

export default AvisosSolicitacao;
